import React, { useCallback, useMemo, useState } from 'react';

import { Space } from 'antd-v5';
import { chain, isEqual, keyBy, sortBy } from 'lodash';
import { DateTime } from 'luxon';
import { MdClose, MdContentPaste } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Button, Heading, Select, Text } from 'scouting-ui/lib';

import type { MBCRecord } from '@appTypes/esb';
import { useMBDict } from '@dict/duck/hooks';
import {
  useAssignMeritBadgeCounselorBadgesMutation,
  useRemoveMeritBadgeCounselorBadgeMutation,
} from '@modules/meritBadgeCounselors/api';
import { useMeritBadgeCounselorProfile } from '@modules/meritBadgeCounselors/hooks';
import { Img, Modal, Spin, Tag } from '@shared/components';
import {
  isMobileSel,
  userDataSel,
} from '@shared/duck/selectors/general.selectors';
import { toastService } from '@toasts';

import styles from './styles.less';

interface EditCounselorMeritBadgesModalProps {
  open: boolean;
  memberId: string | null;
  searchResults: MBCRecord[];
  changeSearchResults: React.Dispatch<React.SetStateAction<MBCRecord[]>>;
  onCloseModal: () => void;
}

type MeritBadgeCounselorBadgeModifications = {
  addMeritBadges: Set<number>;
  removeMeritBadges: Set<number>;
};

const EditCounselorMeritBadgesModal: React.FC<
  EditCounselorMeritBadgesModalProps
> = ({ open, memberId, searchResults, changeSearchResults, onCloseModal }) => {
  const [badgesState, setBadgesState] =
    useState<MeritBadgeCounselorBadgeModifications>({
      addMeritBadges: new Set([]),
      removeMeritBadges: new Set([]),
    });

  const isMobile = useSelector(isMobileSel);
  const { meritBadges: mbsLookup } = useMBDict();
  const { meritBadges, name, loading } = useMeritBadgeCounselorProfile({
    memberId,
    extendedInfo: true,
  });
  const [addBadges, { isLoading: addLoading }] =
    useAssignMeritBadgeCounselorBadgesMutation();
  const [expireBadges, { isLoading: removeLoading }] =
    useRemoveMeritBadgeCounselorBadgeMutation();

  const userData = useSelector(userDataSel);

  const mbsLookupDict = useMemo(() => keyBy(mbsLookup, 'id'), [mbsLookup]);

  const options = chain(mbsLookup)
    .map(mb => ({
      label: (
        <span className={styles.meritBadgeOption}>
          <Img
            className={styles.meritBadgeOptionIcon}
            src={mb.imageUrl100}
            title={mb.name}
          />
          <Text>{mb.name}</Text>
        </span>
      ),
      value: mb.id,
      title: mb.name,
    }))
    .value();

  const hasChanges =
    badgesState.addMeritBadges.size !== 0 ||
    badgesState.removeMeritBadges.size !== 0;

  const meritBadgesToDisplay = useMemo(() => {
    const filteredMbcBadges = meritBadges.filter(
      mb => !badgesState.removeMeritBadges.has(mb.meritBadgeId),
    );
    const addedMbcBadges = [...badgesState.addMeritBadges].map(mbId => {
      const foundBadge = mbsLookupDict[mbId];
      return {
        ...foundBadge,
        meritBadgeId: foundBadge.id,
        meritBadgeName: foundBadge.name,
      };
    });
    return sortBy([...filteredMbcBadges, ...addedMbcBadges], 'meritBadgeName');
  }, [meritBadges, badgesState, mbsLookupDict]);

  const handleAddBadgesToRemove = useCallback(
    (mbId: number) => {
      if (badgesState.addMeritBadges.has(mbId)) {
        return setBadgesState(prev => {
          const copyAddSet = new Set(prev.addMeritBadges);
          copyAddSet.delete(mbId);
          return {
            ...prev,
            addMeritBadges: copyAddSet,
          };
        });
      }
      setBadgesState(prev => ({
        ...prev,
        removeMeritBadges: new Set(prev.removeMeritBadges).add(mbId),
      }));
    },
    [badgesState],
  );

  const handleAddBadgesToAdd = (mbId: number) => {
    setBadgesState(prev => ({
      ...prev,
      addMeritBadges: new Set(prev.addMeritBadges).add(mbId),
    }));
  };

  const handleSaveChanges = useCallback(async () => {
    const personMbs =
      searchResults.find(res => `${res.memberId}` === memberId)?.meritBadges ??
      [];
    let newPersonMbs = [...personMbs];

    if (badgesState.addMeritBadges.size > 0) {
      try {
        const effectiveDt = DateTime.now().toFormat('yyyy-MM-dd');
        const meritBadgesToAdd = [...badgesState.addMeritBadges].map(mbId => ({
          meritBadgeId: mbId,
          effectiveDt,
        }));

        await addBadges({
          payload: [
            {
              memberId: Number(memberId),
              meritBadges: meritBadgesToAdd,
            },
          ],
        }).unwrap();
        newPersonMbs = [
          ...newPersonMbs,
          ...[...badgesState.addMeritBadges].map(mbId => ({
            meritBadgeId: mbId,
            meritBadgeName: mbsLookupDict[mbId]?.name,
            effectiveDt,
            expiryDt: null,
            createdBy: userData?.profile?.memberId,
            createdDt: DateTime.now().toISO({
              includeOffset: false,
            }),
            lastModifiedBy: null,
            lastModifiedDt: null,
          })),
        ];

        toastService.success(
          <FormattedMessage id="editMbcBadges.modal.update.mbAdd.success" />,
        );
      } catch {
        toastService.error(
          <FormattedMessage id="editMbcBadges.modal.update.mbAdd.error" />,
        );
      }
    }

    if (badgesState.removeMeritBadges.size > 0) {
      const meritBadgesToRemove = [...badgesState.removeMeritBadges].map(
        mbId => ({
          meritBadgeId: mbId,
        }),
      );

      try {
        await expireBadges({
          payload: [
            {
              memberId: Number(memberId),
              meritBadges: meritBadgesToRemove,
            },
          ],
        }).unwrap();

        newPersonMbs = newPersonMbs.filter(
          mbs => ![...badgesState.removeMeritBadges].includes(mbs.meritBadgeId),
        );
        toastService.success(
          <FormattedMessage id="editMbcBadges.modal.update.mbRemove.success" />,
        );
      } catch {
        toastService.error(
          <FormattedMessage id="editMbcBadges.modal.update.mbRemove.error" />,
        );
      }
    }
    if (!isEqual(personMbs, newPersonMbs)) {
      const newResults = searchResults.map(result =>
        `${result.memberId}` === memberId
          ? { ...result, meritBadges: newPersonMbs }
          : result,
      );
      changeSearchResults(newResults);
    }
    setBadgesState({
      addMeritBadges: new Set([]),
      removeMeritBadges: new Set([]),
    });
    onCloseModal();
  }, [
    searchResults,
    badgesState,
    onCloseModal,
    memberId,
    addBadges,
    mbsLookupDict,
    userData,
    expireBadges,
    changeSearchResults,
  ]);

  const handleCloseModal = () => {
    setBadgesState({
      addMeritBadges: new Set([]),
      removeMeritBadges: new Set([]),
    });
    onCloseModal();
  };

  const tagList = useMemo(() => {
    if (loading) {
      return (
        <div className={styles.tagList}>
          <Spin />
        </div>
      );
    }

    return meritBadgesToDisplay.length ? (
      <div className={styles.tagList}>
        {meritBadgesToDisplay.map(mb => (
          <Tag key={mb.meritBadgeId} className={styles.mbTag} outline={false}>
            <Img
              key={mb.meritBadgeId}
              className={styles.meritBadgeListIcon}
              src={mb.imageUrl100}
              title={mb.meritBadgeName}
            />
            <Text paragraph={false}>{mb.meritBadgeName}</Text>

            <Button
              shape="circle"
              size="small"
              ghost
              className={styles.smallBtnIcon}
              icon={<MdClose />}
              onClick={() => handleAddBadgesToRemove(mb.meritBadgeId)}
            />
          </Tag>
        ))}
      </div>
    ) : (
      <div className={styles.emptyList}>
        <MdContentPaste className={styles.smallBtnIcon} />
        <Heading font="display-h4" color="color-text-regular">
          <FormattedMessage id="editMbcBadges.modal.emptyList.title" />
        </Heading>
        <Heading font="display-h5" color="color-text-regular">
          <FormattedMessage id="editMbcBadges.modal.emptyList.description" />
        </Heading>
      </div>
    );
  }, [loading, meritBadgesToDisplay, handleAddBadgesToRemove]);

  return (
    <Modal
      mask={!isMobile}
      visible={open}
      width={500}
      className={styles.modal}
      title={<FormattedMessage id="editMbcBadges.modal.title" />}
      onCancel={handleCloseModal}
    >
      <Text paragraph strong className="mb-2">
        <FormattedMessage
          id="editMbcBadges.modal.description"
          values={{ mbcName: loading ? '' : name }}
        />
      </Text>
      <Select
        placeholder={
          <Text>
            <FormattedMessage id="editMbcBadges.modal.select" />
          </Text>
        }
        className={styles.select}
        showSearch
        optionFilterProp="title"
        value={null}
        options={options}
        onSelect={(_, option) => {
          handleAddBadgesToAdd(option.value as number);
        }}
      />
      <Text font="body-caption">
        <FormattedMessage
          id="editMbcBadges.modal.list"
          values={{ amount: meritBadgesToDisplay.length }}
        />
      </Text>
      {tagList}
      <div className={styles.save}>
        <Space direction="horizontal" size={'small'}>
          <Button
            loading={addLoading || removeLoading}
            disabled={!hasChanges}
            onClick={handleSaveChanges}
          >
            <FormattedMessage id="shared.save" />
          </Button>
          <div className={styles.close}>
            <Button
              loading={addLoading || removeLoading}
              onClick={handleCloseModal}
            >
              <FormattedMessage id="shared.close" />
            </Button>
          </div>
        </Space>
      </div>
    </Modal>
  );
};

export default EditCounselorMeritBadgesModal;
